// /redaktionen/$person — verifierbar författarsida per redaktionsmedlem.
// Detta är noden som gör bylinen kontrollerbar: biografi, expertisområden,
// granskningsansvar och sameAs till verifierad profil. Allt läses från
// src/content/editorial.ts så byline, hubbsida och författarsida aldrig
// kan säga olika saker.

import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { breadcrumbs, person, aeoGeo } from "@/lib/seo/schema";
import { abs, SOCIAL_IMAGE_URL } from "@/lib/seo/constants";
import {
  EDITORIAL_HUB_PATH,
  EDITORIAL_POLICY_PATH,
  authorId,
  authorPath,
  getEditorialPerson,
} from "@/content/editorial";

export const Route = createFileRoute("/redaktionen_/$person")({
  loader: ({ params }) => {
    const p = getEditorialPerson(params.person);
    if (!p) throw notFound();
    return { key: params.person, p };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { key, p } = loaderData;
    const path = authorPath(key);
    const title = `${p.name} — ${p.role}`;
    const description =
      p.credentials ??
      `${p.name} är ${p.role.toLowerCase()} på Fenny och står bakom prisdata, guider och branschstudier inom filmproduktion.`;

    const personLd = {
      ...person({
        name: p.name,
        jobTitle: p.role,
        path,
        image: p.image,
        description,
        knowsAbout: p.knowsAbout,
        sameAs: [p.linkedin].filter(Boolean) as string[],
      }),
      "@id": authorId(key),
      mainEntityOfPage: abs(path),
    };

    return {
      meta: [
        { title: `${title} | Fenny` },
        { name: "description", content: description.slice(0, 160) },
        { property: "og:title", content: title },
        { property: "og:description", content: description.slice(0, 160) },
        { property: "og:type", content: "profile" },
        { property: "og:url", content: abs(path) },
        { property: "og:image", content: SOCIAL_IMAGE_URL },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description.slice(0, 160) },
        { name: "twitter:image", content: SOCIAL_IMAGE_URL },
      ],
      links: [{ rel: "canonical", href: abs(path) }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path })) },
        { type: "application/ld+json", children: JSON.stringify(personLd) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbs([
              { name: "Hem", path: "/" },
              { name: "Redaktionen", path: EDITORIAL_HUB_PATH },
              { name: p.name, path },
            ]),
          ),
        },
      ],
    };
  },
  component: AuthorPage,
});

function AuthorPage() {
  const { p } = Route.useLoaderData();

  return (
    <main className="bg-background text-foreground">
      <Section>
        <div className="mx-auto max-w-3xl space-y-8 py-12">
          <nav className="text-sm text-muted-foreground">
            <Link to={EDITORIAL_HUB_PATH} className="underline-offset-2 hover:underline">
              Redaktionen
            </Link>
          </nav>

          <header className="flex flex-wrap items-center gap-4">
            {p.image ? (
              <img
                src={p.image}
                alt={p.name}
                width={72}
                height={72}
                className="h-18 w-18 rounded-full object-cover"
              />
            ) : (
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-mint/15 text-2xl">
                {p.name.charAt(0)}
              </span>
            )}
            <div>
              <h1 className="text-3xl font-semibold">{p.name}</h1>
              <p className="text-muted-foreground">{p.role}</p>
            </div>
          </header>

          {p.credentials && <p className="lede text-lg text-foreground/80">{p.credentials}</p>}

          {p.knowsAbout && p.knowsAbout.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-xl font-semibold">Expertisområden</h2>
              <ul className="flex flex-wrap gap-2">
                {p.knowsAbout.map((k) => (
                  <li
                    key={k}
                    className="rounded-full border border-border px-3 py-1 text-sm text-foreground/80"
                  >
                    {k}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">Så granskas innehållet</h2>
            <p className="text-foreground/80">
              Prisspann, ledtider och format-benchmarks bygger på Fennys egna produktioner.
              Varje faktasida visar när den senast granskades, och rättelser görs löpande.
            </p>
            <Link
              to={EDITORIAL_POLICY_PATH}
              className="text-sm underline underline-offset-4"
            >
              Läs den redaktionella policyn
            </Link>
          </section>

          {p.linkedin && (
            <section className="space-y-2">
              <h2 className="text-xl font-semibold">Verifierad profil</h2>
              <a
                href={p.linkedin}
                target="_blank"
                rel="noopener noreferrer me"
                className="text-sm underline underline-offset-4"
              >
                LinkedIn
              </a>
            </section>
          )}

          <div className="pt-2">
            <Button to="/kontakt">Kontakta redaktionen</Button>
          </div>
        </div>
      </Section>
    </main>
  );
}
